import {
  type Node,
  type UseSortableList,
  type UseSortableListOptions,
} from '../types.js';
import {
  CANCEL_DRAG,
  DISABLE_SORTING,
  ENABLE_SORTING,
  END_DRAG,
  LOAD_DATA,
  START_DRAG,
} from './actionTypes.js';
import {
  initialState,
  type SortableListViewReducer,
  sortableListViewReducer,
} from './reducer.js';
import { safeStringify } from '@contra/utilities/safeStringify';
import {
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { useCallback, useEffect, useReducer, useRef } from 'react';

/**
 * Determines if two arrays of ids string[] are equal in length and in the exact same order.
 * https://gomakethings.com/checking-if-two-arrays-are-equal/
 * @param array1 The first array of ids to compare
 * @param array2 The second array of ids to compare
 * @returns boolean
 */
const idArraysAreEqual = (
  array1: UniqueIdentifier[],
  array2: UniqueIdentifier[],
): boolean => {
  return safeStringify(array1) === safeStringify(array2);
};

export const useSortableListView = <T extends Node>({
  data,
  disabled = false,
  onDataIdsSorted,
}: UseSortableListOptions<T>): UseSortableList<T> => {
  const [state, dispatch] = useReducer<SortableListViewReducer<T>>(
    sortableListViewReducer,
    // This is needed to ensure that the initial (SSR) render is non-empty.
    sortableListViewReducer(
      {
        ...initialState,
        disabled,
      },
      { payload: data, type: LOAD_DATA },
    ),
  );

  // Update state data when parent list changes
  useEffect(() => {
    dispatch({ payload: data, type: LOAD_DATA });
  }, [data]);

  // if the new component gets a new disabled prop,
  // need to make sure that it gets passed down with the hook state.
  useEffect(() => {
    dispatch({ type: disabled ? DISABLE_SORTING : ENABLE_SORTING });
  }, [disabled]);

  // whenever the sorted list of ids is changed
  // we need to notify the server via `onDataIdsSorted`
  const previousSortOrder = useRef<null | UniqueIdentifier[]>(null);

  useEffect(() => {
    const currentSortOrder = state.sortedNodeIds;
    // if the order has changed and we have handler, invoke it.

    if (
      // we have a callback to notify when sort order changes
      onDataIdsSorted &&
      // we have current sort order (changes)
      currentSortOrder &&
      // make sure the list of sorted ids isn't an empty array
      currentSortOrder.length > 0 &&
      // the previous value wasn't null (not yet initialized)
      previousSortOrder.current &&
      // the old and new values are different
      !idArraysAreEqual(currentSortOrder, previousSortOrder.current)
    ) {
      onDataIdsSorted(currentSortOrder);
    }

    // update previous values with current values
    previousSortOrder.current = currentSortOrder;
  }, [state.sortedNodeIds, onDataIdsSorted]);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      dispatch({ payload: { id: event.active.id }, type: START_DRAG });
    },
    [dispatch],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      dispatch({ payload: { active, over }, type: END_DRAG });
    },
    [dispatch],
  );

  const onDragCancel = useCallback(() => {
    dispatch({ type: CANCEL_DRAG });
  }, [dispatch]);

  return {
    activeSortId: state.activeSortId,
    disabled: state.disabled,
    isSorting: state.isSorting,
    sortedData: state.sortedData,
    sortedNodeIds: state.sortedNodeIds,
    sortListeners: {
      onDragCancel,
      onDragEnd,
      onDragStart,
    },
  };
};
